import { FunctionsHttpError } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import type { ItemAlteracao, Nivel, Tipo } from '../utils/alteracoes'

export type Plano = {
  alvo: { nivel: Nivel; nome: string; moeda: string | null; plataforma: string }
  tipo: Tipo
  percentual?: number | null
  itens?: ItemAlteracao[]
  avisos?: string[]
  /** A ação não faz sentido agora (e o porquê). */
  bloqueio?: string
}

export type Pedido = { nivel: Nivel; objetoId: string; tipo: Tipo; percentual: number | null }

/** Erro da função com o plano novo, quando a plataforma mudou entre abrir e confirmar. */
export class PlanoMudou extends Error {
  readonly plano: Plano
  constructor(mensagem: string, plano: Plano) {
    super(mensagem)
    this.plano = plano
  }
}

async function chamar<T>(corpo: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('alterar-campanha', { body: corpo })
  if (error) {
    const detalhe = error instanceof FunctionsHttpError ? await error.context.json().catch(() => null) : null
    if (detalhe?.plano) throw new PlanoMudou(detalhe.erro, detalhe.plano)
    throw new Error(detalhe?.erro ?? error.message)
  }
  return data as T
}

const corpoDe = (p: Pedido) => ({ nivel: p.nivel, objeto_id: p.objetoId, tipo: p.tipo, percentual: p.tipo === 'orcamento' ? p.percentual : undefined })

/** Lê o estado de agora na plataforma e devolve o antes → depois. Não muda nada. */
export const prepararAlteracao = (p: Pedido) => chamar<Plano>({ acao: 'preparar', ...corpoDe(p) })

/** Aplica o plano que a pessoa viu. Se a plataforma mudou, lança PlanoMudou com o plano novo. */
export const aplicarAlteracao = (p: Pedido, esperado: ItemAlteracao[], origem: 'sugestao' | 'manual', motivo: string | null) =>
  chamar<{ status: 'aplicada'; id: string }>({ acao: 'aplicar', ...corpoDe(p), esperado, origem, motivo })

export const desfazerAlteracao = (id: string) => chamar<{ status: 'desfeita'; id: string }>({ acao: 'desfazer', alteracao_id: id })

export type Alteracao = {
  id: string
  marca: string
  plataforma: string
  nivel: Nivel
  nome: string
  tipo: Tipo
  percentual: number | null
  itens: ItemAlteracao[]
  moeda: string | null
  origem: 'sugestao' | 'manual' | 'desfazer'
  motivo: string | null
  desfaz: string | null
  status: 'pendente' | 'aplicada' | 'falhou' | 'desfeita'
  erro: string | null
  email: string
  criadaEm: string
  desfeitaEm: string | null
}

/** O registro das marcas visíveis (o RLS recorta), do mais novo para o mais antigo. */
export async function listarAlteracoes(marcaId: string | null, limite = 200): Promise<Alteracao[]> {
  let q = supabase
    .from('alteracoes')
    .select('id, plataforma, nivel, nome, tipo, percentual, itens, moeda, origem, motivo, desfaz, status, erro, usuario_email, criada_em, desfeita_em, marcas(nome)')
    .order('criada_em', { ascending: false })
    .limit(limite)
  if (marcaId) q = q.eq('marca_id', marcaId)
  const { data, error } = await q
  if (error) throw new Error(error.message)
  return (data ?? []).map((r) => ({
    id: r.id,
    marca: (r.marcas as unknown as { nome: string } | null)?.nome ?? '',
    plataforma: r.plataforma,
    nivel: r.nivel,
    nome: r.nome,
    tipo: r.tipo,
    percentual: r.percentual == null ? null : Number(r.percentual),
    itens: r.itens as ItemAlteracao[],
    moeda: r.moeda,
    origem: r.origem,
    motivo: r.motivo,
    desfaz: r.desfaz,
    status: r.status,
    erro: r.erro,
    email: r.usuario_email,
    criadaEm: r.criada_em,
    desfeitaEm: r.desfeita_em,
  }))
}
